import { useSharedKpiFilters } from "@/lib/kpiFilterMemory";
import { CreateReportButton } from "@/components/CreateReportButton";
import { PageActions } from "@/components/PageActions";
import { Link } from "react-router-dom";
import { LastImportInfo } from "@/components/LastImportInfo";
import { useEffect, useMemo, useState, useRef, useCallback, memo } from "react";
import { projectPeriod, scopeFromFilters } from "@/lib/caProjection";
import React from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { cachedGet, invalidate } from "@/lib/apiCache";
import { eur, toParams } from "@/lib/kpi";
import { List as RwList } from "react-window";
import {
  LineChart, Line, ComposedChart, Bar, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import {
  Download, Upload, TrendingUp, TrendingDown, Target, Calendar,
  Search, ArrowUpDown, ChevronDown, ChevronLeft, ChevronRight, AlertTriangle,
  Trophy, Euro, BarChart3, Percent, Edit, X as XIcon, Save,
} from "lucide-react";
import { KpiTableToolbar, SortHeadCell } from "@/components/kpi/KpiTableToolbar";
import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";
import CaDetailView from "@/pages/ca/CaDetailView";
import CaForecast from "@/pages/ca/forecast/CaForecast";
import CaAnalysis from "@/pages/ca/analysis/CaAnalysis";
import { JustesseBadge, JUSTESSE_TOOLTIP, justesse } from "@/pages/ca/caDetailUtils";
import { LayoutGrid, Table2 } from "lucide-react";

import { OrbitAI } from "@/components/OrbitAI";

/* ─── Palette ─────────────────────────────────────────────────────────── */
const CLR = {
  bg: "var(--bg-app)", card: "#FFFFFF",
  blue: "#2563EB", green: "#16A34A", orange: "#F59E0B", red: "#EF4444",
  text: "#0F172A", muted: "#64748B",
};

const fmtEur = (v) => {
  if (v == null || !isFinite(v)) return "—";
  const n = Math.round(Number(v));
  return new Intl.NumberFormat("fr-FR").format(n) + " €";
};
const fmtEurShort = (v) => {
  if (v == null || !isFinite(v)) return "—";
  return Math.round(Number(v)).toLocaleString("fr-FR").replace(/\u202F/g, " ").replace(/,/g, " ") + " €";
};
const fmtPct = (v) => (v == null || !isFinite(v) ? "—" : (v >= 0 ? "+" : "") + (v * 100).toFixed(1) + "%");
const perfStatus = (perf) => perf >= 1 ? "ok" : perf >= 0.9 ? "warn" : "ko";
const perfChip = (perf) => perf >= 1 ? "bg-emerald-100 text-emerald-700"
                          : perf >= 0.9 ? "bg-amber-100 text-amber-700"
                          : "bg-rose-100 text-rose-700";
const dotColor = (perf) => perf >= 1 ? "bg-emerald-500"
                          : perf >= 0.9 ? "bg-amber-500"
                          : "bg-rose-500";

/* ─── Compact MultiSelect ─────────────────────────────────────────────── */
const MS = memo(function MS({ label, options, value = [], onChange, testid, labels, single = false }) {
  return <FilterMultiSelect label={label} options={options} labelMap={labels} value={value} onChange={onChange} testid={testid} single={single} size="md" topLabel className="flex-1 min-w-[130px]" placeholder={`Tous ${label}`} menuWidth={240}/>;
});

/* ─── Import name warnings dialog ─────────────────────────────────────── */
export function NameWarningsDialog({ data, onClose }) {
  const items = data.items || [];
  const pm = data.previous_month ? new Date(data.previous_month + "-01").toLocaleDateString("fr-FR", { month: "long", year: "numeric" }) : "mois précédent";
  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/40 grid place-items-center p-4" onClick={onClose} data-testid="import-name-warnings">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-amber-50 grid place-items-center shrink-0"><AlertTriangle className="w-5 h-5 text-amber-600"/></div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-slate-900">Noms de corners incohérents détectés</div>
            <div className="text-xs text-slate-500 mt-0.5">{items.length} corner(s) du fichier importé diffèrent de {pm}. Corrigez l'orthographe dans le fichier source puis réimportez, sinon ils apparaîtront en double dans les filtres.</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-slate-100 grid place-items-center" data-testid="import-name-warnings-close"><XIcon className="w-4 h-4"/></button>
        </div>
        <div className="overflow-auto p-3">
          <table className="w-full text-xs">
            <thead><tr className="text-[10px] uppercase text-slate-500 text-left"><th className="px-2 py-1.5">Type</th><th className="px-2 py-1.5">Nom dans le fichier</th><th className="px-2 py-1.5">Nom {pm}</th></tr></thead>
            <tbody>
              {items.map((w, i) => (
                <tr key={i} className="border-t border-slate-100" data-testid={`name-warning-${i}`}>
                  <td className="px-2 py-2"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${w.kind === "similar" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`}>{w.kind === "similar" ? `Faute probable${w.similarity ? ` · ${Math.round(w.similarity * 100)} %` : ""}` : "Renommé"}</span></td>
                  <td className="px-2 py-2 font-semibold text-slate-900">{w.corner_file}</td>
                  <td className="px-2 py-2 text-slate-700">{w.previous_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end"><button onClick={onClose} className="px-4 py-2 rounded-full bg-slate-900 text-white text-xs font-bold" data-testid="import-name-warnings-ok">Compris</button></div>
      </div>
    </div>
  );
}

/* ─── KPI compact card ────────────────────────────────────────────────── */
function KpiCard({ icon: Icon, iconColor = "text-blue-600", iconBg = "bg-blue-50",
                   label, value, sub, subTone = "muted", progress, testid }) {
  const toneCls = subTone === "up" ? "text-emerald-600 font-semibold"
                : subTone === "down" ? "text-rose-600 font-semibold"
                : "text-slate-500";
  return (
    <div className="bg-white rounded-lg border border-slate-200 px-2.5 py-2 flex flex-col gap-1 shadow-[0_1px_3px_rgba(15,23,42,.04)] min-w-0" data-testid={testid}>
      <div className="flex items-center justify-between gap-1.5">
        <span className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider truncate">{label}</span>
        {Icon && <div className={`w-5 h-5 rounded ${iconBg} grid place-items-center shrink-0`}><Icon className={`w-3 h-3 ${iconColor}`}/></div>}
      </div>
      <div className="text-[15px] font-bold text-slate-900 leading-none tabular-nums truncate">{value}</div>
      {progress != null && (
        <div className="h-1 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, Math.max(0, progress * 100))}%`, background: progress >= 1 ? CLR.green : progress >= 0.9 ? CLR.orange : CLR.blue }}/>
        </div>
      )}
      {sub && <div className={`text-[10px] ${toneCls} truncate`}>{sub}</div>}
    </div>
  );
}

/* ─── Filter bar ──────────────────────────────────────────────────────── */
export function FilterBar({ meta, f, setF }) {
  const drOpts = useMemo(() => (meta?.drs || []).slice().sort(), [meta]);
  const secOpts = useMemo(() => {
    const drs = f.drs || [];
    const s = new Set();
    (meta?.corners || []).forEach(c => {
      if (drs.length && !drs.includes(c.dr)) return;
      if (c.secteur) s.add(c.secteur);
    });
    return [...s].sort();
  }, [meta, f.drs]);
  const cornerOpts = useMemo(() => {
    const drs = f.drs || [];
    const secs = f.secteurs || [];
    const keys = [];
    const labels = {};
    (meta?.corners || []).forEach(c => {
      if (drs.length && !drs.includes(c.dr)) return;
      if (secs.length && !secs.includes(c.secteur)) return;
      if (!c.corner_key) return;
      keys.push(c.corner_key);
      labels[c.corner_key] = c.corner;
    });
    return { keys, labels };
  }, [meta, f.drs, f.secteurs]);

  const monthOrder = ["JANVIER","FEVRIER","MARS","AVRIL","MAI","JUIN","JUILLET","AOUT","SEPTEMBRE","OCTOBRE","NOVEMBRE","DECEMBRE"];
  const moOpts = useMemo(() => (meta?.mois || []).slice().sort((a,b) => monthOrder.indexOf(a) - monthOrder.indexOf(b)), [meta]);
  const wkOpts = useMemo(() => {
    if ((meta?.semaines || []).length) return meta.semaines.map(String);
    // Repli : semaines ISO déduites des dates importées (année / mois sélectionnés)
    const selYears = new Set((f.annees || []).map(Number)); const selMonths = new Set((f.mois || []).map(m => monthOrder.indexOf(m) + 1).filter(i => i > 0));
    const s = new Set();
    (meta?.dates || []).forEach(d => {
      if (!d || d.length < 10) return; const y = Number(d.slice(0, 4)), m = Number(d.slice(5, 7));
      if (selYears.size && !selYears.has(y)) return; if (selMonths.size && !selMonths.has(m)) return;
      const dt = new Date(d + "T00:00:00Z"); const day = (dt.getUTCDay() + 6) % 7; dt.setUTCDate(dt.getUTCDate() - day + 3);
      const fy = new Date(Date.UTC(dt.getUTCFullYear(), 0, 4)); s.add(1 + Math.round(((dt - fy) / 86400000 - 3 + ((fy.getUTCDay() + 6) % 7)) / 7));
    });
    return [...s].sort((a, b) => a - b).map(String);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta, f.annees, f.mois]);
  const yrOpts = meta?.annees || [];

  // Days available: from meta.dates, filtered by selected year/month if any
  const jourOpts = useMemo(() => {
    const monthIdx = (m) => monthOrder.indexOf(m) + 1;
    const selYears = new Set((f.annees || []).map(Number));
    const selMonths = new Set((f.mois || []).map(monthIdx).filter(i => i > 0));
    return (meta?.dates || []).filter(d => {
      if (!d || d.length < 10) return false;
      const y = Number(d.slice(0, 4));
      const m = Number(d.slice(5, 7));
      if (selYears.size && !selYears.has(y)) return false;
      if (selMonths.size && !selMonths.has(m)) return false;
      return true;
    });
  }, [meta, f.annees, f.mois]);
  const jourLabels = useMemo(() => {
    const map = {};
    jourOpts.forEach(d => {
      const dt = new Date(d);
      map[d] = dt.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short" });
    });
    return map;
  }, [jourOpts]);

  const onDrs     = useCallback(x => setF(p => ({ ...p, drs: x, secteurs: [], corners: [] })), [setF]);
  const onSec     = useCallback(x => setF(p => ({ ...p, secteurs: x, corners: [] })), [setF]);
  const onCorners = useCallback(x => setF(p => ({ ...p, corners: x })), [setF]);
  const onYear    = useCallback(x => setF(p => ({ ...p, annees: x.map(Number), jours: [] })), [setF]);
  const onExercice = useCallback(x => setF(p => ({ ...p, exercices: x, annees: x.length ? [] : p.annees, jours: [] })), [setF]);
  const onMois    = useCallback(x => setF(p => ({ ...p, mois: x, jours: [] })), [setF]);
  const onWk      = useCallback(x => setF(p => ({ ...p, semaines: x.map(Number) })), [setF]);
  const onJours   = useCallback(x => setF(p => ({ ...p, jours: x, date_from: undefined, date_to: undefined })), [setF]);
  const onRange   = useCallback((k, v) => setF(p => ({ ...p, [k]: v || undefined, jours: [] })), [setF]);
  const reset     = useCallback(() => setF({}), [setF]);
  const nActive = (f.drs?.length || 0) + (f.secteurs?.length || 0) + (f.corners?.length || 0)
                 + (f.annees?.length || 0) + (f.mois?.length || 0) + (f.semaines?.length || 0) + (f.exercices?.length || 0)
                 + (f.jours?.length || 0) + (f.date_from || f.date_to ? 1 : 0);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3 shadow-[0_1px_3px_rgba(15,23,42,.04)]" data-testid="cav2-filter-bar">
      <div className="flex flex-wrap gap-2 items-end">
        <MS label="DR" testid="cav2-f-dr" options={drOpts} value={f.drs || []} onChange={onDrs}/>
        <MS label="Secteur" testid="cav2-f-sec" options={secOpts} value={f.secteurs || []} onChange={onSec}/>
        <MS label="Corner" testid="cav2-f-corner" options={cornerOpts.keys} labels={cornerOpts.labels} value={f.corners || []} onChange={onCorners}/>
        <MS label="Exercice" testid="cav2-f-exercice" options={meta?.exercices || []} value={f.exercices || []} onChange={onExercice}/>
        <MS label="Année" testid="cav2-f-year" options={yrOpts.map(String)} value={(f.annees || []).map(String)} onChange={onYear}/>
        <MS label="Mois" testid="cav2-f-mois" options={moOpts} value={f.mois || []} onChange={onMois}/>
        <MS label="Semaine" testid="cav2-f-wk" options={wkOpts} labels={Object.fromEntries(wkOpts.map(w => [w, `S${String(w).padStart(2, "0")}`]))} value={(f.semaines || []).map(String)} onChange={onWk}/>
        <MS label="Jour" testid="cav2-f-jour" options={jourOpts} labels={jourLabels} value={f.jours || []} onChange={onJours}/>
        <div className="flex flex-col" data-testid="cav2-f-range">
          <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">Période personnalisée</span>
          <div className="flex items-center gap-1">
            <input type="date" value={f.date_from || ""} max={f.date_to || undefined} onChange={e => onRange("date_from", e.target.value)} className="h-8 text-[11px] border border-slate-200 rounded-lg px-1.5 text-slate-700" data-testid="cav2-f-date-from"/>
            <span className="text-[10px] text-slate-400">→</span>
            <input type="date" value={f.date_to || ""} min={f.date_from || undefined} onChange={e => onRange("date_to", e.target.value)} className="h-8 text-[11px] border border-slate-200 rounded-lg px-1.5 text-slate-700" data-testid="cav2-f-date-to"/>
          </div>
        </div>
        {nActive > 0 && (
          <button onClick={reset} data-testid="cav2-f-reset"
                  className="ml-auto text-[11px] font-semibold text-slate-500 hover:text-slate-800 px-2 py-1 rounded hover:bg-slate-100">
            Réinitialiser ({nActive})
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── Sticky-header table row (react-window) ─────────────────────────── */
const ROW_H = 44;
const CornerRow = memo(function CornerRow({ index, style, rows, onRowClick, gridCols }) {
  const c = rows[index];
  if (!c) return null;
  const perf = c.objectif ? c.realise / c.objectif : 0;
  const ecart = (c.realise || 0) - (c.objectif || 0);
  const statusLabel = perf >= 1 ? "En avance" : perf >= 0.9 ? "Dans l'objectif" : "En retard";
  const bg = index % 2 === 0 ? "bg-white" : "bg-slate-50/40";
  return (
    <div style={{ ...style, display: "grid", gridTemplateColumns: gridCols }}
         className={`text-[11px] items-center border-b border-slate-100 hover:bg-blue-50/30 cursor-pointer transition-colors ${bg}`}
         onClick={() => onRowClick && onRowClick(c)}
         data-testid={`cav2-row-${c.corner_key}`}
         data-corner={c.corner}
         role="row">
      <div className="pl-3 flex items-center gap-2 min-w-0">
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotColor(perf)}`}/>
        <div className="min-w-0">
          <div className="font-semibold text-slate-800 truncate leading-tight text-[12px]">{c.corner}</div>
          <div className="text-[9px] text-slate-400 truncate">{c.secteur || "—"}</div>
        </div>
      </div>
      <div className="text-slate-600 truncate px-1.5">{c.enseigne || "—"}</div>
      <div className="text-slate-600 truncate px-1.5">{c.bm || "—"}</div>
      <div className="text-right px-1.5 tabular-nums text-[11px]">
        {c.weather ? (
          <span className="inline-flex items-center gap-1" title={c.weather.label || ""}>
            <span className="text-sm leading-none">{c.weather.emoji || "❓"}</span>
            <span className="text-slate-700 font-semibold">{c.weather.tmax != null ? `${Math.round(c.weather.tmax)}°` : ""}</span>
          </span>
        ) : <span className="text-slate-300">—</span>}
      </div>
      <div className="text-right tabular-nums font-semibold text-slate-900 px-1.5">{fmtEur(c.realise)}</div>
      <div className="text-right tabular-nums text-slate-500 px-1.5">{fmtEur(c.objectif)}</div>
      <div className={`text-right tabular-nums font-semibold px-1.5 ${ecart >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{ecart >= 0 ? "+" : ""}{fmtEur(ecart)}</div>
      <div className="text-right px-1.5">
        <span className={`px-1.5 py-0.5 rounded font-bold text-[10px] ${perfChip(perf)}`}>{c.objectif ? Math.round(perf * 100) + "%" : "—"}</span>
      </div>
      <div className="text-right px-1.5"><JustesseBadge realise={c.realise} trend3m={c.trend3m} n_1={c.n_1} objectif={c.objectif} testid={`cav2-justesse-${c.corner_key}`}/></div>
      <div className={`text-right tabular-nums text-[10px] font-semibold px-1.5 ${(c.evolution_n1 || 0) >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{fmtPct(c.evolution_n1)}</div>
      <div className={`text-right tabular-nums text-[10px] font-semibold px-1.5 ${c.lfl_pct == null ? "text-slate-400" : (c.lfl_pct >= 0 ? "text-emerald-600" : "text-rose-600")}`}>{c.lfl_pct == null ? "—" : fmtPct(c.lfl_pct)}</div>
      <div className={`text-right tabular-nums text-[10px] font-semibold px-1.5 ${c.lfl_eur == null ? "text-slate-400" : (c.lfl_eur >= 0 ? "text-emerald-600" : "text-rose-600")}`}>{c.lfl_eur == null ? "—" : (c.lfl_eur >= 0 ? "+" : "") + fmtEur(c.lfl_eur)}</div>
      <div className="pr-3 text-right">
        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${perfChip(perf)}`}>{statusLabel}</span>
      </div>
    </div>
  );
});

function CornersTable({ corners, selectedDate, onRowClick, viewMode, setViewMode, status, setStatus, onExportView }) {
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("perf");
  const [sortDir, setSortDir] = useState("desc");

  const filteredSorted = useMemo(() => {
    const s = q.trim().toLowerCase();
    let arr = corners.filter(c => {
      if (s && !((c.corner || "").toLowerCase().includes(s) || (c.enseigne || "").toLowerCase().includes(s) || (c.bm || "").toLowerCase().includes(s))) return false;
      const perf = c.objectif ? c.realise / c.objectif : 0;
      if (status === "avance") return perf >= 1;
      if (status === "objectif") return perf >= 0.9 && perf < 1;
      if (status === "retard") return perf < 0.9;
      return true;
    });
    const dir = sortDir === "asc" ? 1 : -1;
    const cmp = (a, b) => {
      let va, vb;
      switch (sortKey) {
        case "corner":   va = (a.corner||"").toLowerCase(); vb = (b.corner||"").toLowerCase(); break;
        case "enseigne": va = (a.enseigne||"").toLowerCase(); vb = (b.enseigne||"").toLowerCase(); break;
        case "bm":       va = (a.bm||"").toLowerCase(); vb = (b.bm||"").toLowerCase(); break;
        case "weather":  va = a.weather?.tmax != null ? a.weather.tmax : -999;
                         vb = b.weather?.tmax != null ? b.weather.tmax : -999; break;
        case "ca":       va = a.realise||0; vb = b.realise||0; break;
        case "objectif": va = a.objectif||0; vb = b.objectif||0; break;
        case "ecart":    va = (a.realise||0)-(a.objectif||0); vb = (b.realise||0)-(b.objectif||0); break;
        case "n1":       va = a.evolution_n1||0; vb = b.evolution_n1||0; break;
        case "lfl_pct":  va = a.lfl_pct == null ? -Infinity : a.lfl_pct; vb = b.lfl_pct == null ? -Infinity : b.lfl_pct; break;
        case "lfl_eur":  va = a.lfl_eur == null ? -Infinity : a.lfl_eur; vb = b.lfl_eur == null ? -Infinity : b.lfl_eur; break;
        case "statut":   va = a.objectif ? a.realise/a.objectif : 0; vb = b.objectif ? b.realise/b.objectif : 0; break;
        case "justesse": va = justesse(a.realise, a.trend3m, a.n_1, a.objectif)?.idx ?? -Infinity; vb = justesse(b.realise, b.trend3m, b.n_1, b.objectif)?.idx ?? -Infinity; break;
        case "perf":
        default: va = a.objectif ? a.realise/a.objectif : 0; vb = b.objectif ? b.realise/b.objectif : 0;
      }
      if (typeof va === "string") return va.localeCompare(vb) * dir;
      return (va - vb) * dir;
    };
    return arr.sort(cmp);
  }, [corners, q, sortKey, sortDir, status]);

  const toggleSort = (k) => {
    if (sortKey === k) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortKey(k);
      setSortDir("desc");
    }
  };

  // HeadCell inline (déclaration en module non nécessaire ici — on render directement)
  const renderHead = (k, label, align = "left", title) => (
    <div className={`py-1.5 flex items-center ${align === "right" ? "justify-end pr-3" : "pl-3"}`} key={`h-${k}-${label}`}>
      <SortHeadCell k={k} label={label} align={align} title={title}
                    sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}
                    testid={`cav2-th-${k}`}/>
    </div>
  );

  const GRID_COLS = "minmax(180px,1.4fr) minmax(80px,.7fr) minmax(100px,.9fr) 80px 100px 100px 100px 70px 95px 80px 80px 110px 100px";

  const totals = useMemo(() => {
    const t = { realise: 0, objectif: 0, n_1: 0, trend3m: 0, lfl_realise: 0, lfl_n1: 0, lfl_count: 0 };
    filteredSorted.forEach(c => {
      t.realise += c.realise || 0;
      t.objectif += c.objectif || 0;
      t.n_1 += c.n_1 || 0;
      t.trend3m += c.trend3m || 0;
      // Isoperimeter LFL — only corners with N-1 data
      if ((c.n_1 || 0) > 0) {
        t.lfl_realise += c.realise || 0;
        t.lfl_n1 += c.n_1 || 0;
        t.lfl_count += 1;
      }
    });
    t.ecart = t.realise - t.objectif;
    t.perf = t.objectif ? t.realise / t.objectif : 0;
    t.n1 = t.n_1 ? (t.realise - t.n_1) / t.n_1 : 0;
    t.lfl_pct = t.lfl_n1 ? (t.lfl_realise - t.lfl_n1) / t.lfl_n1 : null;
    t.lfl_eur = t.lfl_count > 0 ? (t.lfl_realise - t.lfl_n1) : null;
    return t;
  }, [filteredSorted]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 flex flex-col shadow-[0_1px_3px_rgba(15,23,42,.04)]" data-testid="cav2-table">
      <div className="p-4 border-b border-slate-100 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div>
            <h2 className="font-display text-base font-bold text-slate-900">
              {viewMode === "global" && "Vue Globale — Total agrégé"}
              {viewMode === "corner" && `Corners ${selectedDate ? `du ${new Date(selectedDate).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}` : ""}`}
              {viewMode === "secteur" && "Vue par Secteur"}
              {viewMode === "dr" && "Vue par Directeur Régional"}
            </h2>
            <p className="text-[11px] text-slate-500">
              {viewMode === "global" ? "1 ligne cumulée" : `${filteredSorted.length} / ${corners.length} ${viewMode === "corner" ? "corners" : viewMode === "secteur" ? "secteurs" : "DR"}`}
            </p>
          </div>
          {setViewMode && (
            <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5" data-testid="cav2-view-toggle">
              {[
                { k: "global",  l: "Vue Globale" },
                { k: "corner",  l: "Vue Corner" },
                { k: "secteur", l: "Vue Secteur" },
                { k: "dr",      l: "Vue DR" },
              ].map(t => (
                <button key={t.k} onClick={() => setViewMode(t.k)}
                        data-testid={`cav2-view-${t.k}`}
                        className={`text-[11px] px-2.5 py-1 rounded-md font-semibold ${viewMode === t.k ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>
                  {t.l}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher…"
                   data-testid="cav2-table-search"
                   className="pl-8 pr-3 py-1.5 rounded-lg border border-slate-200 text-xs w-56 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"/>
          </div>
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
            {[
              { k: "all",       l: "Tous",           c: "" },
              { k: "avance",    l: "En avance",      c: "text-emerald-700" },
              { k: "objectif",  l: "Dans l'objectif",c: "text-amber-700" },
              { k: "retard",    l: "En retard",      c: "text-rose-700" },
            ].map(t => (
              <button key={t.k} onClick={() => setStatus(t.k)}
                      data-testid={`cav2-status-${t.k}`}
                      className={`text-[11px] px-2.5 py-1 rounded-md font-semibold ${status === t.k ? "bg-white shadow-sm text-slate-900" : `text-slate-500 hover:text-slate-800 ${t.c}`}`}>
                {t.l}
              </button>
            ))}
          </div>
          {onExportView && (
            <button onClick={() => onExportView(filteredSorted)} data-testid="cav2-export-view-btn"
                    className="flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700">
              <Download className="w-3.5 h-3.5"/> Exporter la vue
            </button>
          )}
        </div>
      </div>

      {/* Sticky header */}
      <div className="grid border-b border-slate-200 bg-slate-50" style={{ gridTemplateColumns: GRID_COLS }}>
        {renderHead("corner", viewMode === "corner" ? "Corner" : viewMode === "secteur" ? "Secteur" : viewMode === "dr" ? "Directeur Régional" : "Périmètre")}
        {renderHead("enseigne", viewMode === "corner" ? "Enseigne" : "Corners")}
        {renderHead("bm", "Responsable")}
        {renderHead("weather", "Météo", "right")}
        {renderHead("ca", "CA Réalisé", "right")}
        {renderHead("objectif", "Objectif", "right")}
        {renderHead("ecart", "Écart", "right")}
        {renderHead("perf", "Perf.", "right")}
        {renderHead("justesse", "Justesse obj.", "right", JUSTESSE_TOOLTIP)}
        {renderHead("n1", "VS N-1", "right")}
        {renderHead("lfl_pct", "LFL %", "right")}
        {renderHead("lfl_eur", "LFL €", "right")}
        {renderHead("statut", "Statut", "right")}
      </div>

      {/* Total row */}
      {filteredSorted.length > 0 && (
        <div className="grid text-[11px] items-center border-b-2 border-slate-300 bg-slate-100 font-bold" style={{ gridTemplateColumns: GRID_COLS }} data-testid="cav2-table-total">
          <div className="pl-3 text-slate-900 text-[12px] font-bold">TOTAL ({filteredSorted.length})</div>
          <div className="text-slate-400 px-1.5">—</div>
          <div className="text-slate-400 px-1.5">—</div>
          <div className="text-slate-400 px-1.5 text-right">—</div>
          <div className="text-right tabular-nums text-slate-900 px-1.5">{fmtEur(totals.realise)}</div>
          <div className="text-right tabular-nums text-slate-700 px-1.5">{fmtEur(totals.objectif)}</div>
          <div className={`text-right tabular-nums px-1.5 ${totals.ecart >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{totals.ecart >= 0 ? "+" : ""}{fmtEur(totals.ecart)}</div>
          <div className="text-right px-1.5">
            <span className={`px-1.5 py-0.5 rounded text-[10px] ${perfChip(totals.perf)}`}>{totals.objectif ? Math.round(totals.perf * 100) + "%" : "—"}</span>
          </div>
          <div className="text-right px-1.5"><JustesseBadge realise={totals.realise} trend3m={totals.trend3m} n_1={totals.n_1} objectif={totals.objectif} testid="cav2-justesse-total"/></div>
          <div className={`text-right tabular-nums text-[10px] px-1.5 ${totals.n1 >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{fmtPct(totals.n1)}</div>
          <div className={`text-right tabular-nums text-[10px] px-1.5 ${totals.lfl_pct == null ? "text-slate-400" : (totals.lfl_pct >= 0 ? "text-emerald-700" : "text-rose-700")}`}>{totals.lfl_pct == null ? "—" : fmtPct(totals.lfl_pct)}</div>
          <div className={`text-right tabular-nums text-[10px] px-1.5 ${totals.lfl_eur == null ? "text-slate-400" : (totals.lfl_eur >= 0 ? "text-emerald-700" : "text-rose-700")}`}>{totals.lfl_eur == null ? "—" : (totals.lfl_eur >= 0 ? "+" : "") + fmtEur(totals.lfl_eur)}</div>
          <div className="text-slate-400 pr-3 text-right">—</div>
        </div>
      )}

      {/* Virtualized body */}
      {filteredSorted.length === 0 ? (
        <div className="p-12 text-center text-slate-400 text-sm italic">Aucun corner ne correspond aux filtres.</div>
      ) : (
        <div style={{ height: Math.min(560, filteredSorted.length * ROW_H) }}>
          <RwList
            rowCount={filteredSorted.length}
            rowHeight={ROW_H}
            defaultHeight={560}
            rowComponent={CornerRow}
            rowProps={{ rows: filteredSorted, gridCols: GRID_COLS, onRowClick }}
          />
        </div>
      )}
    </div>
  );
}

/* ─── Horizontal calendar strip ──────────────────────────────────────── */
function CalendarStrip({ byDay, selectedDate, setSelectedDate }) {
  const [view, setView] = useState("month"); // month | week
  const [monthOffset, setMonthOffset] = useState(0);
  const [weekOffset, setWeekOffset] = useState(0);

  const dailyByDate = useMemo(() => {
    const m = {};
    (byDay || []).forEach(d => {
      if (!d.date) return;
      const k = d.date.slice(0, 10);
      m[k] = d;
    });
    return m;
  }, [byDay]);

  // Anchor on last available data date (Hugo data may not match real "today")
  const anchor = useMemo(() => {
    const dates = (byDay || []).map(d => d.date).filter(Boolean).sort();
    return dates.length ? new Date(dates[dates.length - 1]) : new Date();
  }, [byDay]);

  const days = useMemo(() => {
    if (view === "week") {
      const base = new Date(anchor); base.setDate(base.getDate() + weekOffset * 7);
      const dow = (base.getDay() + 6) % 7;
      const monday = new Date(base); monday.setDate(base.getDate() - dow);
      return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(monday); d.setDate(monday.getDate() + i);
        return { iso: d.toISOString().slice(0, 10), d, day: d.getDate(), weekday: d.toLocaleDateString("fr-FR", { weekday: "short" }) };
      });
    }
    const base = new Date(anchor.getFullYear(), anchor.getMonth() + monthOffset, 1);
    const y = base.getFullYear(); const mo = base.getMonth();
    const daysInMonth = new Date(y, mo + 1, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => {
      const d = new Date(y, mo, i + 1);
      return { iso: d.toISOString().slice(0, 10), d, day: d.getDate(), weekday: d.toLocaleDateString("fr-FR", { weekday: "short" }) };
    });
  }, [view, anchor, monthOffset, weekOffset]);

  const periodLabel = useMemo(() => {
    if (!days.length) return "";
    if (view === "week") {
      const dt = new Date(days[0].d.valueOf());
      const target = new Date(dt.valueOf()); const dow = (dt.getDay() + 6) % 7;
      target.setDate(target.getDate() - dow + 3);
      const ft = new Date(target.getFullYear(), 0, 4);
      const wk = 1 + Math.round(((target - ft) / 86400000 - 3 + ((ft.getDay() + 6) % 7)) / 7);
      return `Semaine ${wk} · ${days[0].day}–${days[6].day} ${days[0].d.toLocaleDateString("fr-FR", { month: "short" })}`;
    }
    return days[0].d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  }, [days, view]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3 shadow-[0_1px_3px_rgba(15,23,42,.04)]" data-testid="cav2-calendar">
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <div className="flex items-center gap-0.5 bg-slate-100 rounded-md p-0.5">
          <button onClick={() => setView("month")} data-testid="cav2-cal-view-month"
                  className={`text-[11px] font-semibold px-2.5 py-1 rounded ${view === "month" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>Mois</button>
          <button onClick={() => setView("week")} data-testid="cav2-cal-view-week"
                  className={`text-[11px] font-semibold px-2.5 py-1 rounded ${view === "week" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>Semaine</button>
        </div>
        <button onClick={() => view === "week" ? setWeekOffset(v => v - 1) : setMonthOffset(v => v - 1)}
                className="p-1 rounded hover:bg-slate-100 text-slate-600" data-testid="cal-prev">
          <ChevronLeft className="w-4 h-4"/>
        </button>
        <span className="font-semibold text-slate-800 text-sm capitalize min-w-[180px]">{periodLabel}</span>
        <button onClick={() => view === "week" ? setWeekOffset(v => v + 1) : setMonthOffset(v => v + 1)}
                className="p-1 rounded hover:bg-slate-100 text-slate-600" data-testid="cal-next">
          <ChevronRight className="w-4 h-4"/>
        </button>
        {selectedDate && (
          <button onClick={() => setSelectedDate(null)} className="ml-auto text-[11px] text-blue-600 hover:underline font-semibold" data-testid="cal-clear">
            Voir tout
          </button>
        )}
      </div>
      <div className="flex gap-1.5 overflow-x-auto scrollbar-thin pb-2">
        {days.map(d => {
          const dd = dailyByDate[d.iso];
          const perf = dd?.objectif ? (dd.realise || 0) / dd.objectif : null;
          const sel = selectedDate === d.iso;
          const clr = perf == null ? "bg-slate-200"
                    : perf >= 1 ? "bg-emerald-500"
                    : perf >= 0.9 ? "bg-amber-500"
                    : "bg-rose-500";
          return (
            <button key={d.iso}
                    onClick={() => setSelectedDate(sel ? null : d.iso)}
                    data-testid={`cav2-cal-${d.iso}`}
                    className={`shrink-0 ${view === "week" ? "w-24" : "w-14"} h-16 rounded-lg border flex flex-col items-center justify-center gap-1 transition-colors ${sel ? "bg-blue-600 border-blue-600 text-white shadow-md" : "bg-white border-slate-200 hover:border-blue-300 text-slate-700"}`}>
              <span className={`text-[9px] font-semibold uppercase ${sel ? "text-blue-100" : "text-slate-400"}`}>{d.weekday.slice(0,3)}</span>
              <span className="font-bold text-base leading-none">{d.day}</span>
              <span className={`w-2 h-2 rounded-full ${sel ? "bg-white" : clr}`}/>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Standalone panels (top perf, sites à surveiller, IA insights) ───── */
function TopPerformersCard({ topPerformers }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-[0_1px_3px_rgba(15,23,42,.04)]" data-testid="cav2-top-perf">
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="w-4 h-4 text-amber-500"/>
        <h3 className="font-bold text-slate-800 text-sm">Top 5 Performers</h3>
      </div>
      <div className="space-y-1.5">
        {topPerformers.map((c, i) => (
          <div key={c.corner_key} className="flex items-center gap-2 text-[12px] py-1.5">
            <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-800 grid place-items-center font-bold text-[10px] shrink-0">{i + 1}</span>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-slate-800 truncate">{c.corner}</div>
              <div className="text-[10px] text-slate-400 truncate">{c.bm || "—"}</div>
            </div>
            <span className="text-emerald-600 font-bold text-[11px] tabular-nums">+{Math.round((c.pct_atteinte - 1) * 100)}%</span>
            <span className="text-slate-500 font-semibold text-[11px] tabular-nums w-20 text-right">{fmtEur(c.realise)}</span>
          </div>
        ))}
        {topPerformers.length === 0 && <div className="text-xs text-slate-400 italic text-center py-2">Aucun corner performant.</div>}
      </div>
    </div>
  );
}

function ASurveillerCard({ aSurveiller }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-[0_1px_3px_rgba(15,23,42,.04)]" data-testid="cav2-a-surveiller">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="w-4 h-4 text-rose-500"/>
        <h3 className="font-bold text-slate-800 text-sm">Sites à surveiller</h3>
      </div>
      <div className="space-y-1.5">
        {aSurveiller.map((c) => {
          const perf = c.objectif ? c.realise / c.objectif : 0;
          const ecart = (c.realise || 0) - (c.objectif || 0);
          return (
            <div key={c.corner_key} className="flex items-center gap-2 text-[12px] py-1.5">
              <span className="w-2 h-2 rounded-full bg-rose-500 shrink-0"/>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-800 truncate">{c.corner}</div>
                <div className="text-[10px] text-slate-400 truncate">{c.bm || "—"}</div>
              </div>
              <span className="text-rose-600 font-bold text-[11px] tabular-nums">{Math.round(perf * 100)}%</span>
              <span className="text-rose-500 font-semibold text-[11px] tabular-nums w-20 text-right">{fmtEur(ecart)}</span>
            </div>
          );
        })}
        {aSurveiller.length === 0 && <div className="text-xs text-slate-400 italic text-center py-2">Rien à surveiller 🎉</div>}
      </div>
    </div>
  );
}

function InsightsIaCard({ aiInsights, aiLoading, onGenerateAi }) {
  return (
    <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-100 p-4 shadow-[0_1px_3px_rgba(15,23,42,.04)] h-full" data-testid="cav2-insights-ia">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <OrbitAI className="w-4 h-4 text-purple-600"/>
          <h3 className="font-bold text-slate-800 text-sm">Analyse IA — Aujourd'hui</h3>
        </div>
        <button onClick={onGenerateAi} disabled={aiLoading}
                data-testid="cav2-ai-refresh"
                className="text-[10px] font-semibold text-purple-700 hover:underline disabled:opacity-50">
          {aiLoading ? "…" : "Actualiser"}
        </button>
      </div>
      <div className="space-y-2 text-[12px] text-slate-700">
        {(aiInsights || []).length === 0 && !aiLoading && <div className="italic text-slate-400 text-center py-2">Cliquez sur « Actualiser » pour générer.</div>}
        {aiLoading && <div className="animate-pulse text-slate-500 text-center py-2">Analyse en cours…</div>}
        {(aiInsights || []).map((line, i) => (
          <div key={i} className="flex items-start gap-2">
            <span className="text-purple-500 mt-0.5">•</span>
            <span>{line}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Right sidebar ──────────────────────────────────────────────────── */
function Sidebar({ topPerformers, aSurveiller, aiInsights, aiLoading, onGenerateAi }) {
  return (
    <div className="space-y-4" data-testid="cav2-sidebar">
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-[0_1px_3px_rgba(15,23,42,.04)]">
        <div className="flex items-center gap-2 mb-3">
          <Trophy className="w-4 h-4 text-amber-500"/>
          <h3 className="font-bold text-slate-800 text-sm">Top 5 Performers</h3>
        </div>
        <div className="space-y-1.5">
          {topPerformers.map((c, i) => (
            <div key={c.corner_key} className="flex items-center gap-2 text-[12px] py-1.5">
              <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-800 grid place-items-center font-bold text-[10px] shrink-0">{i + 1}</span>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-800 truncate">{c.corner}</div>
              </div>
              <span className="text-emerald-600 font-bold text-[11px] tabular-nums">+{Math.round((c.pct_atteinte - 1) * 100)}%</span>
              <span className="text-slate-500 font-semibold text-[11px] tabular-nums w-20 text-right">{fmtEur(c.realise)}</span>
            </div>
          ))}
          {topPerformers.length === 0 && <div className="text-xs text-slate-400 italic text-center py-2">Aucun corner performant.</div>}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-[0_1px_3px_rgba(15,23,42,.04)]">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-rose-500"/>
          <h3 className="font-bold text-slate-800 text-sm">Sites à surveiller</h3>
        </div>
        <div className="space-y-1.5">
          {aSurveiller.map((c, i) => {
            const perf = c.objectif ? c.realise / c.objectif : 0;
            const ecart = (c.realise || 0) - (c.objectif || 0);
            return (
              <div key={c.corner_key} className="flex items-center gap-2 text-[12px] py-1.5">
                <span className="w-2 h-2 rounded-full bg-rose-500 shrink-0"/>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-800 truncate">{c.corner}</div>
                </div>
                <span className="text-rose-600 font-bold text-[11px] tabular-nums">{Math.round(perf * 100)}%</span>
                <span className="text-rose-500 font-semibold text-[11px] tabular-nums w-20 text-right">{fmtEur(ecart)}</span>
              </div>
            );
          })}
          {aSurveiller.length === 0 && <div className="text-xs text-slate-400 italic text-center py-2">Rien à surveiller 🎉</div>}
        </div>
      </div>

      <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-100 p-4 shadow-[0_1px_3px_rgba(15,23,42,.04)]">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <OrbitAI className="w-4 h-4 text-purple-600"/>
            <h3 className="font-bold text-slate-800 text-sm">Insights IA – Aujourd'hui</h3>
          </div>
          <button onClick={onGenerateAi} disabled={aiLoading}
                  data-testid="cav2-ai-refresh"
                  className="text-[10px] font-semibold text-purple-700 hover:underline disabled:opacity-50">
            {aiLoading ? "…" : "Actualiser"}
          </button>
        </div>
        <div className="space-y-2 text-[12px] text-slate-700">
          {(aiInsights || []).length === 0 && !aiLoading && <div className="italic text-slate-400 text-center py-2">Cliquez sur « Actualiser » pour générer.</div>}
          {aiLoading && <div className="animate-pulse text-slate-500 text-center py-2">Analyse en cours…</div>}
          {(aiInsights || []).map((line, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="text-purple-500 mt-0.5">•</span>
              <span>{line}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Bottom : heatmap + donut ───────────────────────────────────────── */
function HeatmapCard({ byCorner, rows7d, view, setView }) {
  // Show ALL DATES from the current data range (was: last 7). Horizontal scroll on overflow.
  const days = useMemo(() => {
    const set = new Set();
    (rows7d || []).forEach(r => { if (r.date) set.add(r.date.slice(0, 10)); });
    return [...set].sort();
  }, [rows7d]);

  // Aggregate rows by (view group) × day
  const { groupKeys, perfMap } = useMemo(() => {
    const map = {};
    (rows7d || []).forEach(r => {
      if (!r.date) return;
      const d = r.date.slice(0, 10);
      if (!days.includes(d)) return;
      let key;
      if (view === "region") key = r.region || "Autre";
      else if (view === "secteur") key = r.secteur || "Autre";
      else key = r.corner || r.corner_key || "Autre";
      map[key] = map[key] || {};
      map[key][d] = map[key][d] || { realise: 0, objectif: 0 };
      map[key][d].realise += r.realise || 0;
      map[key][d].objectif += r.objectif || 0;
    });
    let keys = Object.keys(map).sort();
    return { groupKeys: keys, perfMap: map };
  }, [rows7d, days, view]);

  const cellClr = (perf) => perf == null ? "bg-slate-100"
                : perf >= 1 ? "bg-emerald-500"
                : perf >= 0.95 ? "bg-emerald-300"
                : perf >= 0.9 ? "bg-amber-300"
                : perf >= 0.8 ? "bg-rose-300"
                : "bg-rose-500";

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-[0_1px_3px_rgba(15,23,42,.04)]" data-testid="cav2-heatmap">
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 className="w-4 h-4 text-blue-600"/>
        <h3 className="font-bold text-slate-800 text-sm">Heatmap performance</h3>
        <div className="ml-2 flex items-center gap-0.5 bg-slate-100 rounded-md p-0.5">
          {[
            { k: "region",  l: "Région" },
            { k: "secteur", l: "Secteur" },
            { k: "corner",  l: "Corner" },
          ].map(t => (
            <button key={t.k} onClick={() => setView(t.k)}
                    data-testid={`cav2-heat-${t.k}`}
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded ${view === t.k ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>
              {t.l}
            </button>
          ))}
        </div>
        <span className="text-[11px] text-slate-500 ml-auto">{days.length} jour{days.length > 1 ? "s" : ""} · glisser →</span>
      </div>
      <div className="overflow-x-auto overflow-y-hidden pb-1" data-testid="cav2-heatmap-scroll">
        <div className="min-w-max">
          <div className="grid gap-1 items-center sticky top-0 bg-white z-10 pb-1"
               style={{ gridTemplateColumns: `140px repeat(${days.length}, 28px)` }}>
            <span></span>
            {days.map(d => (
              <span key={d} className="text-[9px] font-semibold text-slate-400 text-center capitalize leading-tight">
                <div>{new Date(d).toLocaleDateString("fr-FR", { weekday: "short" })}</div>
                <div className="text-slate-600">{new Date(d).getDate()}</div>
              </span>
            ))}
          </div>
          <div className="max-h-[360px] overflow-y-auto space-y-1 pt-1">
            {groupKeys.map(key => (
              <div key={key} className="grid gap-1 items-center"
                   style={{ gridTemplateColumns: `140px repeat(${days.length}, 28px)` }}>
                <span className="text-[10px] font-semibold text-slate-700 truncate sticky left-0 bg-white pr-1" title={key}>{key}</span>
                {days.map(d => {
                  const cell = perfMap[key]?.[d];
                  const perf = cell?.objectif ? cell.realise / cell.objectif : null;
                  return <div key={d} className={`h-5 rounded ${cellClr(perf)}`} title={`${key} · ${d}${perf != null ? ` · ${Math.round(perf*100)}%` : ""}`}/>;
                })}
              </div>
            ))}
            {groupKeys.length === 0 && <div className="text-xs text-slate-400 italic text-center py-4">Aucune donnée.</div>}
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-3 text-[10px] text-slate-500 flex-wrap">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500"/>≥100%</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-300"/>≥95%</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-300"/>≥90%</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-300"/>≥80%</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-500"/>&lt;80%</span>
      </div>
    </div>
  );
}

function DonutCard({ byCorner }) {
  const data = useMemo(() => {
    const groups = {};
    byCorner.forEach(c => {
      const key = (c.enseigne || "").trim();
      if (!key || /^autres?$/i.test(key)) return;
      groups[key] = (groups[key] || 0) + (c.realise || 0);
    });
    return Object.entries(groups)
      .map(([name, value]) => ({ name, value }))
      .filter(x => x.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [byCorner]);

  const total = data.reduce((s, x) => s + x.value, 0);
  const COLORS = [CLR.blue, CLR.green, CLR.orange, "#8B5CF6", "#EC4899", "#0EA5E9", "#84CC16", "#F97316", "#14B8A6", "#6366F1"];

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-[0_1px_3px_rgba(15,23,42,.04)]" data-testid="cav2-donut">
      <div className="flex items-center gap-2 mb-3">
        <Target className="w-4 h-4 text-blue-600"/>
        <h3 className="font-bold text-slate-800 text-sm">Répartition du CA par enseigne</h3>
      </div>
      <div className="grid grid-cols-2 items-center gap-4">
        <div className="relative w-full h-40">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={data} innerRadius={45} outerRadius={72} paddingAngle={2} dataKey="value">
                {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]}/>)}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <div className="text-lg font-bold text-slate-900 leading-none">{fmtEur(total)}</div>
            <div className="text-[10px] text-slate-400 mt-1">CA total</div>
          </div>
        </div>
        <div className="space-y-1.5">
          {data.map((d, i) => (
            <div key={d.name} className="flex items-center gap-2 text-[12px]">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }}/>
              <span className="flex-1 text-slate-700 truncate">{d.name}</span>
              <span className="font-bold text-slate-900 tabular-nums">{total ? Math.round(d.value * 100 / total) + "%" : "—"}</span>
            </div>
          ))}
          {data.length === 0 && <div className="text-xs text-slate-400 italic">Aucune donnée.</div>}
        </div>
      </div>
    </div>
  );
}

/* ─── Saisie CA modal ─────────────────────────────────────────────── */
function SaisieCaModal({ onClose }) {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(null);
  const load = () => {
    api.get("/kpi/ca-daily/today-rows", { params: { date } })
       .then(r => setRows(r.data?.rows || [])).catch(() => setRows([]));
  };
  useEffect(load, [date]);
  const saveOne = async (r, field, value) => {
    setSaving(r.corner_key + "|" + field);
    try {
      await api.patch("/kpi/ca-daily/manual", { corner_key: r.corner_key, date, [field]: Number(value) });
      invalidate("/kpi/ca-daily");
      toast.success("Enregistré");
      load();
    } catch { toast.error("Erreur"); }
    finally { setSaving(null); }
  };
  const filtered = q ? rows.filter(r => (r.corner || "").toLowerCase().includes(q.toLowerCase())) : rows;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="cav2-saisie-modal">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[85vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-display font-bold text-slate-900 flex items-center gap-2"><Edit className="w-4 h-4 text-emerald-600"/> Saisie manuelle du CA</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="cav2-saisie-close"><XIcon className="w-4 h-4"/></button>
        </div>
        <div className="p-3 border-b border-slate-100 flex items-center gap-2">
          <label className="text-xs font-semibold text-slate-500">Date :</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
                 className="px-2 py-1 rounded border border-slate-200 text-sm"/>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher…"
                 className="ml-auto px-2 py-1 rounded border border-slate-200 text-sm w-56"/>
        </div>
        <div className="overflow-auto flex-1">
          <table className="text-xs w-full">
            <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Corner</th>
                <th className="text-right px-3 py-2 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Objectif</th>
                <th className="text-right px-3 py-2 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Réalisé</th>
                <th className="text-right px-3 py-2 font-bold text-slate-500 uppercase tracking-wider text-[10px]">N-1</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.corner_key} className="border-b border-slate-100 hover:bg-blue-50/30">
                  <td className="px-3 py-1.5"><div className="font-semibold text-slate-800 truncate">{r.corner}</div><div className="text-[9px] text-slate-400">{r.secteur || "—"}</div></td>
                  <td className="text-right px-3 py-1.5 tabular-nums text-slate-500">{fmtEur(r.objectif)}</td>
                  <td className="text-right px-3 py-1.5">
                    <input defaultValue={r.realise || 0} type="number"
                           onBlur={e => Number(e.target.value) !== (r.realise || 0) && saveOne(r, "realise", e.target.value)}
                           className={`w-24 px-2 py-1 rounded border text-right tabular-nums text-xs ${saving === r.corner_key+"|realise" ? "border-blue-400 bg-blue-50" : "border-slate-200"}`}
                           data-testid={`saisie-input-${r.corner_key}`}/>
                  </td>
                  <td className="text-right px-3 py-1.5 tabular-nums text-slate-500">{fmtEur(r.n_1)}</td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-slate-400 italic">Aucun corner.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ─── ISO week helper ──────────────────────────────────────────────── */
function isoWeek(dateStr) {
  const dt = new Date(dateStr);
  if (isNaN(dt)) return { year: 0, week: 0 };
  const target = new Date(dt.valueOf());
  const dow = (dt.getDay() + 6) % 7;
  target.setDate(target.getDate() - dow + 3);
  const ft = new Date(target.getFullYear(), 0, 4);
  const wk = 1 + Math.round(((target - ft) / 86400000 - 3 + ((ft.getDay() + 6) % 7)) / 7);
  return { year: target.getFullYear(), week: wk };
}

/* ─── Drilldown side panel — history + weekly subtotals ────────────── */
function DrilldownPanel({ target, allRows, weatherMap, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!target) return;
    if (target.type === "corner") {
      api.get("/kpi/ca-daily/corner-history", { params: { corner_key: target.value, days: 90 } })
         .then(r => setD(r.data)).catch(() => setD({ history: [], corner: target.label || target.value }));
      return;
    }
    // For secteur / DR / global: aggregate from allRows client-side
    const byDate = {};
    (allRows || []).forEach(r => {
      if (!r.date) return;
      if (target.type !== "global") {
        if ((r[target.type] || "—") !== target.value) return;
      }
      const k = r.date.slice(0, 10);
      byDate[k] = byDate[k] || { date: k, realise: 0, objectif: 0, n_1: 0, trend3m: 0 };
      byDate[k].realise += r.realise || 0;
      byDate[k].objectif += r.objectif || 0;
      byDate[k].n_1 += r.n_1 || 0;
      byDate[k].trend3m += r.trend3m || 0;
    });
    const history = Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
    setD({
      corner: target.label || target.value,
      secteur: target.type === "secteur" ? target.value : "Multi",
      bm: target.type === "dr" ? target.value : "—",
      enseigne: "Multi",
      history,
      n_days: history.length,
    });
  }, [target, allRows]);
  const chartData = (d?.history || []).map(h => ({
    date: (h.date || "").slice(5, 10),
    Réalisé: h.realise || 0,
    Objectif: h.objectif || 0,
    "N-1": h.n_1 || 0,
  }));
  const total = (d?.history || []).reduce((acc, h) => ({
    realise: acc.realise + (h.realise || 0),
    objectif: acc.objectif + (h.objectif || 0),
    n_1: acc.n_1 + (h.n_1 || 0),
    trend3m: acc.trend3m + (h.trend3m || 0),
  }), { realise: 0, objectif: 0, n_1: 0, trend3m: 0 });

  /* Group history by ISO week (chronological) and inject weekly subtotals */
  const groupedRows = useMemo(() => {
    const out = [];
    const hist = (d?.history || []).slice();
    if (!hist.length) return out;
    let curWk = null;
    let acc = { realise: 0, objectif: 0, n_1: 0, trend3m: 0, count: 0 };
    for (const h of hist) {
      const w = isoWeek(h.date);
      const key = `${w.year}-W${w.week}`;
      if (curWk === null) curWk = key;
      if (key !== curWk) {
        out.push({ _type: "subtotal", label: `Sous-total ${curWk.split("-")[1]}`, ...acc });
        curWk = key;
        acc = { realise: 0, objectif: 0, n_1: 0, trend3m: 0, count: 0 };
      }
      out.push({ _type: "day", ...h });
      acc.realise += h.realise || 0;
      acc.objectif += h.objectif || 0;
      acc.n_1 += h.n_1 || 0;
      acc.trend3m += h.trend3m || 0;
      acc.count += 1;
    }
    if (curWk) out.push({ _type: "subtotal", label: `Sous-total ${curWk.split("-")[1]}`, ...acc });
    out.push({ _type: "total", label: "TOTAL", realise: total.realise, objectif: total.objectif, n_1: total.n_1, trend3m: total.trend3m });
    return out;
  }, [d, total]);

  if (!target) return null;
  const typeLabel = target.type === "corner" ? "Corner"
                  : target.type === "secteur" ? "Secteur"
                  : target.type === "dr" ? "Directeur Régional"
                  : "Périmètre global";
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="cav2-drilldown">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}/>
      <div className="relative bg-white w-full max-w-lg h-full shadow-2xl flex flex-col overflow-y-auto">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wider text-blue-600">{typeLabel}</div>
            <h2 className="font-display font-bold text-slate-900 truncate">{d?.corner || target.label || target.value}</h2>
            <p className="text-[11px] text-slate-500 truncate">{d?.secteur || "—"} · {d?.bm || "—"} · {d?.enseigne || "—"}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="cav2-drilldown-close"><XIcon className="w-4 h-4"/></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="text-[10px] font-bold text-slate-500 uppercase">Réalisé total</div>
              <div className="font-bold text-slate-900 tabular-nums">{fmtEur(total.realise)}</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="text-[10px] font-bold text-slate-500 uppercase">Objectif total</div>
              <div className="font-bold text-slate-900 tabular-nums">{fmtEur(total.objectif)}</div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-3">
            <div className="text-xs font-semibold text-slate-700 mb-2">Historique</div>
            <div className="h-56">
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
                  <XAxis dataKey="date" tick={{ fontSize: 9, fill: CLR.muted }} axisLine={false} tickLine={false}/>
                  <YAxis tickFormatter={fmtEurShort} tick={{ fontSize: 9, fill: CLR.muted }} axisLine={false} tickLine={false} width={45}/>
                  <Tooltip formatter={v => fmtEur(v)} contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 6, fontSize: 11 }}/>
                  <Legend wrapperStyle={{ fontSize: 10 }} iconType="line"/>
                  <Line type="monotone" dataKey="Objectif" stroke={CLR.orange} strokeDasharray="4 3" strokeWidth={1.5} dot={false}/>
                  <Line type="monotone" dataKey="Réalisé" stroke={CLR.blue} strokeWidth={2} dot={{ r: 2 }}/>
                  <Line type="monotone" dataKey="N-1" stroke="#94A3B8" strokeWidth={1} dot={false}/>
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="cav2-drilldown-detail">
            <div className="text-xs font-semibold text-slate-700 p-3 border-b border-slate-100">Détail avec sous-totaux hebdomadaires</div>
            <div className="max-h-[420px] overflow-y-auto">
              <table className="w-full text-[11px]">
                <thead className="bg-slate-50 sticky top-0"><tr>
                  <th className="text-left px-3 py-1 font-bold text-slate-500 uppercase text-[9px]">Date</th>
                  <th className="text-center px-2 py-1 font-bold text-slate-500 uppercase text-[9px]">Météo</th>
                  <th className="text-right px-3 py-1 font-bold text-slate-500 uppercase text-[9px]">Réalisé</th>
                  <th className="text-right px-3 py-1 font-bold text-slate-500 uppercase text-[9px]">Obj.</th>
                  <th className="text-right px-3 py-1 font-bold text-slate-500 uppercase text-[9px]">Perf</th>
                  <th className="text-right px-3 py-1 font-bold text-slate-500 uppercase text-[9px] cursor-help" title={JUSTESSE_TOOLTIP} data-testid="drilldown-th-justesse">Justesse obj.</th>
                </tr></thead>
                <tbody>
                  {groupedRows.map((row, i) => {
                    const perf = row.objectif ? row.realise / row.objectif : 0;
                    if (row._type === "subtotal") {
                      return (
                        <tr key={`sub-${i}`} className="bg-blue-50 border-t border-b border-blue-200 font-semibold" data-testid="drilldown-subtotal">
                          <td className="px-3 py-1 text-blue-800">{row.label}</td>
                          <td className="px-2 py-1"/>
                          <td className="text-right px-3 py-1 tabular-nums text-blue-900">{fmtEur(row.realise)}</td>
                          <td className="text-right px-3 py-1 tabular-nums text-blue-700">{fmtEur(row.objectif)}</td>
                          <td className={`text-right px-3 py-1 tabular-nums ${perf >= 1 ? "text-emerald-700" : perf >= 0.9 ? "text-amber-700" : "text-rose-700"}`}>{row.objectif ? Math.round(perf * 100) + "%" : "—"}</td>
                          <td className="text-right px-3 py-1"><JustesseBadge realise={row.realise} trend3m={row.trend3m} n_1={row.n_1} objectif={row.objectif} testid={`drilldown-justesse-sub-${i}`}/></td>
                        </tr>
                      );
                    }
                    if (row._type === "total") {
                      return (
                        <tr key="total" className="bg-slate-900 text-white font-bold border-t-2 border-slate-700" data-testid="drilldown-total">
                          <td className="px-3 py-1.5">{row.label}</td>
                          <td className="px-2 py-1.5"/>
                          <td className="text-right px-3 py-1.5 tabular-nums">{fmtEur(row.realise)}</td>
                          <td className="text-right px-3 py-1.5 tabular-nums text-slate-300">{fmtEur(row.objectif)}</td>
                          <td className={`text-right px-3 py-1.5 tabular-nums ${perf >= 1 ? "text-emerald-300" : perf >= 0.9 ? "text-amber-300" : "text-rose-300"}`}>{row.objectif ? Math.round(perf * 100) + "%" : "—"}</td>
                          <td className="text-right px-3 py-1.5"><JustesseBadge realise={row.realise} trend3m={row.trend3m} n_1={row.n_1} objectif={row.objectif} testid="drilldown-justesse-total"/></td>
                        </tr>
                      );
                    }
                    // Weather cell for daily row (corner-scoped)
                    const wx = weatherMap && target?.type === "corner" && weatherMap[target.value] ? weatherMap[target.value][row.date] : null;
                    return (
                      <tr key={row.date} className="border-b border-slate-100">
                        <td className="px-3 py-1 text-slate-600">{row.date}</td>
                        <td className="text-center px-2 py-1">
                          {wx ? (
                            <span className="inline-flex items-center gap-1" title={wx.label || ""}>
                              <span className="text-sm leading-none">{wx.emoji || "❓"}</span>
                              <span className="text-[10px] text-slate-600 font-semibold">{wx.tmax != null ? `${Math.round(wx.tmax)}°` : ""}</span>
                            </span>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="text-right px-3 py-1 tabular-nums font-semibold text-slate-900">{fmtEur(row.realise)}</td>
                        <td className="text-right px-3 py-1 tabular-nums text-slate-500">{fmtEur(row.objectif)}</td>
                        <td className={`text-right px-3 py-1 tabular-nums font-semibold ${perf >= 1 ? "text-emerald-600" : perf >= 0.9 ? "text-amber-600" : "text-rose-600"}`}>{row.objectif ? Math.round(perf * 100) + "%" : "—"}</td>
                        <td className="text-right px-3 py-1"><JustesseBadge realise={row.realise} trend3m={row.trend3m} n_1={row.n_1} objectif={row.objectif} testid={`drilldown-justesse-${row.date}`}/></td>
                      </tr>
                    );
                  })}
                  {groupedRows.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-slate-400 italic">Aucune donnée.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Page ──────────────────────────────────────────────────────── */
const LS_KEY = "orbit_cav2_filters";

export default function KpiCADailyV2() {
  const { user } = useAuth();
  const isAdmin = user && (user.role === "admin" || user.role === "controller");
  const fileRef = useRef(null);

  const [meta, setMeta] = useState(null);
  const [f, setF] = useSharedKpiFilters({}, { meta, pageKey: LS_KEY });
  const [defaultsSet, setDefaultsSet] = useState(false);

  const [data, setData] = useState({ rows: [], summary: {} });
  const [byDay, setByDay] = useState([]);
  const [byCorner, setByCorner] = useState([]);
  const [weatherMap, setWeatherMap] = useState({}); // {corner_key: {date: {emoji,tmax,label}}}
  const [loading, setLoading] = useState(false);

  const [selectedDate, setSelectedDate] = useState(null);
  const [chartView, setChartView] = useState("jours");
  const [calendarView, setCalendarView] = useState("month"); // month | week
  const [heatView, setHeatView] = useState("region");        // region | secteur | corner
  const [importing, setImporting] = useState(false);
  const [nameWarnings, setNameWarnings] = useState(null); // {items, previous_month}
  const [aiInsights, setAiInsights] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [saisieOpen, setSaisieOpen] = useState(false);
  const [drilldown, setDrilldown] = useState(null); // {type, value, label}
  const [viewMode, setViewMode] = useState("corner"); // corner | secteur | dr
  const [tableStatus, setTableStatus] = useState("all"); // all | avance | objectif | retard
  const [pageTab, setPageTab] = useState(() => (new URLSearchParams(window.location.search).get("tab") === "forecast" ? "forecast" : (localStorage.getItem("orbit_cav2_tab") || "synthese")));
  useEffect(() => { localStorage.setItem("orbit_cav2_tab", pageTab); }, [pageTab]);
  const prevPerfRef = useRef({});

  // Persist filters (hook partagé)

  const periodKey = JSON.stringify({ a: f.annees || [], m: f.mois || [], s: f.semaines || [], j: f.jours || [], y: f.year_mode || "" });
  useEffect(() => {
    const p = toParams(f);
    const params = { annees: p.annees, mois: p.mois, semaines: p.semaines, jours: p.jours, year_mode: p.year_mode };
    cachedGet("/kpi/ca-daily/meta", { params, ttl: 60_000 }).then(m => {
      setMeta(m);
      // Purge selections not present in the imported file(s) of this period
      const drs = new Set(m?.drs || []); const secs = new Set(m?.secteurs || []); const cks = new Set((m?.corners || []).map(c => c.corner_key));
      setF(prev => {
        const nd = (prev.drs || []).filter(x => drs.has(x)); const ns = (prev.secteurs || []).filter(x => secs.has(x)); const nc = (prev.corners || []).filter(x => cks.has(x));
        if (nd.length === (prev.drs || []).length && ns.length === (prev.secteurs || []).length && nc.length === (prev.corners || []).length) return prev;
        return { ...prev, drs: nd, secteurs: ns, corners: nc };
      });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodKey]);

  useEffect(() => {
    if (defaultsSet || !meta) return;
    const years = meta.annees || [];
    if (years.length && !(f.annees?.length)) setF(p => ({ ...p, annees: [years[years.length - 1]] }));
    setDefaultsSet(true);
  }, [defaultsSet, meta, f.annees]);

  useEffect(() => {
    if (!defaultsSet) return;
    // Fetch weather map for the visible date range (once byDay is loaded)
    const dates = (byDay || []).map(d => d.date).filter(Boolean).sort();
    if (dates.length === 0) return;
    const from = dates[0].slice(0, 10);
    const to = dates[dates.length - 1].slice(0, 10);
    api.get("/weather/ca-daily", { params: { date_from: from, date_to: to } })
       .then(r => setWeatherMap(r.data?.weather || {}))
       .catch(() => setWeatherMap({}));
  }, [defaultsSet, byDay]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    const t = setTimeout(() => {
      const params = toParams(f);
      Promise.all([
        cachedGet("/kpi/ca-daily", { params, signal: controller.signal }),
        cachedGet("/kpi/ca-daily/by-day", { params, signal: controller.signal }),
        cachedGet("/kpi/ca-daily/by-corner", { params, signal: controller.signal }),
      ]).then(([d1, d2, d3]) => {
        // Filter results to Hugo corners only (in case backend still returns raw kpi_daily_ca rows)
        const hugoKeys = new Set((meta?.corners || []).map(c => c.corner_key));
        setData({ ...d1, rows: (d1?.rows || []).filter(r => !r.corner_key || hugoKeys.has(r.corner_key)) });
        setByDay(d2 || []);
        const trendMap = {};
        (d1?.rows || []).forEach(r => { trendMap[r.corner_key] = (trendMap[r.corner_key] || 0) + (r.trend3m || 0); });
        // Isoperimeter LFL — only when N-1 data exists for that corner
        setByCorner((d3 || [])
          .filter(c => !c.corner_key || hugoKeys.has(c.corner_key))
          .map(c => {
            const hasN1 = (c.n_1 || 0) > 0;
            return {
              ...c,
              trend3m: trendMap[c.corner_key] || 0,
              lfl_pct: hasN1 ? (c.realise - c.n_1) / c.n_1 : null,
              lfl_eur: hasN1 ? (c.realise - c.n_1) : null,
            };
          }));
      }).catch(() => {}).finally(() => setLoading(false));
    }, 200);
    return () => { clearTimeout(t); controller.abort(); };
  }, [f, defaultsSet, meta]);

  /* Corners filtered by selected date (from calendar) */
  const cornersForDate = useMemo(() => {
    if (!selectedDate) return byCorner;
    // Aggregate rows for that day into corner shape
    const per = {};
    (data.rows || []).forEach(r => {
      if (!r.date || r.date.slice(0, 10) !== selectedDate) return;
      const k = r.corner_key;
      if (!per[k]) {
        per[k] = { corner: r.corner, corner_key: k, secteur: r.secteur, bm: r.bm, dr: r.dr, region: r.region,
                   enseigne: r.enseigne, realise: 0, objectif: 0, n_1: 0, trend3m: 0 };
      }
      per[k].realise += r.realise || 0;
      per[k].objectif += r.objectif || 0;
      per[k].n_1 += r.n_1 || 0;
      per[k].trend3m += r.trend3m || 0;
    });
    // Merge context from byCorner (enseigne, moyenne)
    const ctx = Object.fromEntries(byCorner.map(c => [c.corner_key, c]));
    return Object.values(per).map(c => {
      const hasN1 = (c.n_1 || 0) > 0;
      return {
        ...c,
        enseigne: c.enseigne || ctx[c.corner_key]?.enseigne,
        evolution_n1: c.n_1 ? (c.realise - c.n_1) / c.n_1 : 0,
        pct_atteinte: c.objectif ? c.realise / c.objectif : 0,
        lfl_pct: hasN1 ? (c.realise - c.n_1) / c.n_1 : null,
        lfl_eur: hasN1 ? (c.realise - c.n_1) : null,
      };
    });
  }, [selectedDate, data.rows, byCorner]);

  /* Rows aggregated by view mode (global | corner | secteur | dr) */
  const rowsForView = useMemo(() => {
    // Add weather to corners based on selectedDate (or latest date with data)
    const today = new Date().toISOString().slice(0, 10);
    const wxDate = selectedDate || (byDay || []).filter(d => (d.realise || 0) > 0 && d.date <= today).map(d => d.date).sort().reverse()[0] || (byDay || []).map(d => d.date).filter(d => d <= today).sort().reverse()[0];
    const enrichWeather = (list) => list.map(c => {
      const wx = wxDate && weatherMap[c.corner_key] ? weatherMap[c.corner_key][wxDate] : null;
      return { ...c, weather: wx };
    });
    if (viewMode === "corner") return enrichWeather(cornersForDate);
    if (viewMode === "global") {
      const g = cornersForDate.reduce((a, c) => ({
        realise: a.realise + (c.realise || 0),
        objectif: a.objectif + (c.objectif || 0),
        n_1: a.n_1 + (c.n_1 || 0),
        trend3m: a.trend3m + (c.trend3m || 0),
        lfl_realise: a.lfl_realise + ((c.n_1 || 0) > 0 ? (c.realise || 0) : 0),
        lfl_n1: a.lfl_n1 + ((c.n_1 || 0) > 0 ? (c.n_1 || 0) : 0),
        lfl_count: a.lfl_count + ((c.n_1 || 0) > 0 ? 1 : 0),
        _count: a._count + 1,
      }), { realise: 0, objectif: 0, n_1: 0, trend3m: 0, lfl_realise: 0, lfl_n1: 0, lfl_count: 0, _count: 0 });
      if (!g._count) return [];
      return [{
        corner: "Périmètre total",
        corner_key: "global:all",
        _group: "global",
        _groupValue: "all",
        secteur: "Tous",
        dr: "Tous",
        bm: "—",
        enseigne: `${g._count} corner${g._count > 1 ? "s" : ""}`,
        realise: g.realise, objectif: g.objectif, n_1: g.n_1, trend3m: g.trend3m, _count: g._count,
        pct_atteinte: g.objectif ? g.realise / g.objectif : 0,
        evolution_n1: g.n_1 ? (g.realise - g.n_1) / g.n_1 : 0,
        lfl_pct: g.lfl_n1 ? (g.lfl_realise - g.lfl_n1) / g.lfl_n1 : null,
        lfl_eur: g.lfl_count > 0 ? (g.lfl_realise - g.lfl_n1) : null,
      }];
    }
    const field = viewMode; // "secteur" or "dr"
    const map = {};
    cornersForDate.forEach(c => {
      const k = c[field] || "—";
      if (!map[k]) {
        map[k] = {
          corner: k,
          corner_key: `${viewMode}:${k}`,
          _group: viewMode,
          _groupValue: k,
          secteur: viewMode === "secteur" ? k : "—",
          dr: viewMode === "dr" ? k : "—",
          bm: viewMode === "dr" ? k : "—",
          enseigne: "",
          realise: 0, objectif: 0, n_1: 0, trend3m: 0,
          lfl_realise: 0, lfl_n1: 0, lfl_count: 0,
          _count: 0,
        };
      }
      map[k].realise += c.realise || 0;
      map[k].objectif += c.objectif || 0;
      map[k].n_1 += c.n_1 || 0;
      map[k].trend3m += c.trend3m || 0;
      if ((c.n_1 || 0) > 0) {
        map[k].lfl_realise += c.realise || 0;
        map[k].lfl_n1 += c.n_1 || 0;
        map[k].lfl_count += 1;
      }
      map[k]._count += 1;
    });
    return Object.values(map).map(g => ({
      ...g,
      enseigne: `${g._count} corner${g._count > 1 ? "s" : ""}`,
      pct_atteinte: g.objectif ? g.realise / g.objectif : 0,
      evolution_n1: g.n_1 ? (g.realise - g.n_1) / g.n_1 : 0,
      lfl_pct: g.lfl_n1 ? (g.lfl_realise - g.lfl_n1) / g.lfl_n1 : null,
      lfl_eur: g.lfl_count > 0 ? (g.lfl_realise - g.lfl_n1) : null,
    }));
  }, [cornersForDate, viewMode, weatherMap, byDay, selectedDate]);

  /* Detect corners crossing 100% and celebrate (toast in-app) */
  useEffect(() => {
    if (!byCorner?.length) return;
    const prev = prevPerfRef.current;
    const next = {};
    const crossed = [];
    byCorner.forEach(c => {
      const p = c.objectif ? c.realise / c.objectif : 0;
      next[c.corner_key] = p;
      const pp = prev[c.corner_key];
      if (pp != null && pp < 1 && p >= 1) crossed.push({ ...c, perf: p });
    });
    prevPerfRef.current = next;
    if (crossed.length) {
      crossed.slice(0, 5).forEach(c => {
        toast.success(`🎉 ${c.corner} vient d'atteindre 100% de son objectif !`, {
          description: `Réalisé ${fmtEur(c.realise)} · Objectif ${fmtEur(c.objectif)} · ${c.bm || "—"}`,
          duration: 8000,
        });
      });
    }
  }, [byCorner]);

  /* KPI values */
  const s = data?.summary || {};
  const perf = s.objectif ? (s.realise || 0) / s.objectif : 0;
  const ecart = (s.realise || 0) - (s.objectif || 0);
  const nDays = s.n_days || 0;
  const moyJour = nDays ? (s.realise || 0) / nDays : 0;
  const moyMois = moyJour * 30;

  /* LFL isopérimètre — uniquement corners présents dans N et N-1 */
  const lflGlobal = useMemo(() => {
    let realise = 0, n_1 = 0, count = 0;
    (byCorner || []).forEach(c => {
      if ((c.n_1 || 0) > 0) {
        realise += c.realise || 0;
        n_1 += c.n_1 || 0;
        count += 1;
      }
    });
    return {
      pct: n_1 ? (realise - n_1) / n_1 : null,
      eur: count > 0 ? (realise - n_1) : null,
      count,
    };
  }, [byCorner]);

  /* Projection = période en cours (semaine / mois / année) selon les filtres */
  const projection = useMemo(() => projectPeriod(s.realise || 0, byDay, scopeFromFilters(f)).projection, [byDay, s.realise, f.semaines, f.mois]);

  /* Chart data */
  const chartData = useMemo(() => {
    if (!byDay?.length) return [];
    if (chartView === "jours") {
      return byDay.slice(-31).map(d => ({
        date: d.date?.slice(0, 10),
        label: d.date ? new Date(d.date).getDate() : "",
        Réalisé: d.realise || 0,
        Objectif: d.objectif || 0,
        "N-1": d.n_1 || 0,
      }));
    }
    // Aggregate by week
    if (chartView === "semaines") {
      const wks = {};
      byDay.forEach(d => {
        if (!d.date) return;
        const dt = new Date(d.date);
        const target = new Date(dt.valueOf()); const dow = (dt.getDay() + 6) % 7;
        target.setDate(target.getDate() - dow + 3);
        const ft = new Date(target.getFullYear(), 0, 4);
        const wk = 1 + Math.round(((target - ft) / 86400000 - 3 + ((ft.getDay() + 6) % 7)) / 7);
        const key = `${target.getFullYear()}-W${wk}`;
        wks[key] = wks[key] || { label: `S${wk}`, Réalisé: 0, Objectif: 0, "N-1": 0 };
        wks[key].Réalisé += d.realise || 0;
        wks[key].Objectif += d.objectif || 0;
        wks[key]["N-1"] += d.n_1 || 0;
      });
      return Object.values(wks).slice(-13);
    }
    // Aggregate by month
    const mos = {};
    byDay.forEach(d => {
      if (!d.date) return;
      const dt = new Date(d.date);
      const key = `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}`;
      mos[key] = mos[key] || { label: dt.toLocaleDateString("fr-FR",{month:"short"}), Réalisé: 0, Objectif: 0, "N-1": 0 };
      mos[key].Réalisé += d.realise || 0;
      mos[key].Objectif += d.objectif || 0;
      mos[key]["N-1"] += d.n_1 || 0;
    });
    return Object.values(mos).slice(-12);
  }, [byDay, chartView]);

  /* Top 5 / à surveiller */
  const topPerformers = useMemo(() =>
    byCorner.filter(c => c.realise > 0 && c.pct_atteinte > 0)
            .slice().sort((a, b) => b.pct_atteinte - a.pct_atteinte).slice(0, 5),
    [byCorner]);
  const aSurveiller = useMemo(() =>
    byCorner.filter(c => c.realise > 0 && c.objectif > 0)
            .slice().sort((a, b) => a.pct_atteinte - b.pct_atteinte).slice(0, 5),
    [byCorner]);

  /* Auto-compute local AI insights (compact, cheap, deterministic) */
  const localInsights = useMemo(() => {
    const arr = [];
    const over20 = byCorner.filter(c => c.pct_atteinte >= 1.2).length;
    const under90 = byCorner.filter(c => c.realise > 0 && c.pct_atteinte < 0.9).length;
    if (over20) arr.push(`${over20} corners dépassent leur objectif de +20%`);
    if (under90) arr.push(`${under90} corners sous les 90% de l'objectif — à surveiller`);
    if (s.evolution_n1 != null) arr.push(`Évolution N-1 globale : ${fmtPct(s.evolution_n1)}`);
    return arr;
  }, [byCorner, s.evolution_n1]);

  const generateAi = useCallback(() => {
    setAiLoading(true);
    api.post("/kpi/ca-daily/ai-insights", toParams(f)).then(r => {
      const lines = Array.isArray(r.data?.insights) ? r.data.insights : (typeof r.data === "string" ? r.data.split("\n") : []);
      setAiInsights(lines.filter(x => x && String(x).trim().length > 0));
    }).catch(() => setAiInsights(localInsights)).finally(() => setAiLoading(false));
  }, [f, localInsights]);

  useEffect(() => { setAiInsights(localInsights); }, [localInsights]);

  /* Export CSV — respecte la vue courante (viewMode + selectedDate + statut) */
  const onExport = useCallback((rowsOverride) => {
    // Build client-side CSV from the currently visible table rows
    const source = rowsOverride && rowsOverride.length ? rowsOverride : rowsForView;
    // Apply status filter if not provided by table
    let rows = source;
    if (!rowsOverride) {
      rows = source.filter(c => {
        const perf = c.objectif ? c.realise / c.objectif : 0;
        if (tableStatus === "avance") return perf >= 1;
        if (tableStatus === "objectif") return perf >= 0.9 && perf < 1;
        if (tableStatus === "retard") return perf < 0.9;
        return true;
      });
    }
    if (!rows.length) { toast.error("Aucune ligne à exporter"); return; }
    const sep = ";";
    const esc = v => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const isGroup = viewMode !== "corner";
    const cols = isGroup
      ? [viewMode === "secteur" ? "Secteur" : "Directeur Régional", "Corners", "CA Réalisé", "Objectif", "Écart", "Perf %", "Justesse obj.", "Indice justesse %", "VS N-1", "LFL %", "LFL €"]
      : ["Corner", "Enseigne", "Responsable (BM)", "Secteur", "DR", "CA Réalisé", "Objectif", "Écart", "Perf %", "Justesse obj.", "Indice justesse %", "VS N-1", "LFL %", "LFL €"];
    const header = cols.map(esc).join(sep);
    const lines = [header];
    const jCols = (r) => { const j = justesse(r.realise, r.trend3m, r.n_1, r.objectif); return j ? [j.label, j.idx.toFixed(1).replace(".", ",")] : ["", ""]; };
    rows.forEach(c => {
      const perf = c.objectif ? c.realise / c.objectif : 0;
      const ecart = (c.realise || 0) - (c.objectif || 0);
      const perfStr = c.objectif ? (perf * 100).toFixed(1).replace(".", ",") : "";
      const n1Str = c.evolution_n1 != null && isFinite(c.evolution_n1) ? (c.evolution_n1 * 100).toFixed(1).replace(".", ",") : "";
      const lflPctStr = c.lfl_pct != null && isFinite(c.lfl_pct) ? (c.lfl_pct * 100).toFixed(1).replace(".", ",") : "";
      const lflEurStr = c.lfl_eur != null && isFinite(c.lfl_eur) ? Math.round(c.lfl_eur) : "";
      const arr = isGroup
        ? [c.corner, c._count || "", Math.round(c.realise || 0), Math.round(c.objectif || 0), Math.round(ecart), perfStr, ...jCols(c), n1Str, lflPctStr, lflEurStr]
        : [c.corner, c.enseigne || "", c.bm || "", c.secteur || "", c.dr || "",
           Math.round(c.realise || 0), Math.round(c.objectif || 0), Math.round(ecart),
           perfStr, ...jCols(c), n1Str, lflPctStr, lflEurStr];
      lines.push(arr.map(esc).join(sep));
    });
    // Add total row
    const t = rows.reduce((a, c) => ({
      realise: a.realise + (c.realise || 0),
      objectif: a.objectif + (c.objectif || 0),
      n_1: a.n_1 + (c.n_1 || 0),
      trend3m: a.trend3m + (c.trend3m || 0),
      lfl_realise: a.lfl_realise + ((c.n_1 || 0) > 0 ? (c.realise || 0) : 0),
      lfl_n1: a.lfl_n1 + ((c.n_1 || 0) > 0 ? (c.n_1 || 0) : 0),
      lfl_count: a.lfl_count + ((c.n_1 || 0) > 0 ? 1 : 0),
    }), { realise: 0, objectif: 0, n_1: 0, trend3m: 0, lfl_realise: 0, lfl_n1: 0, lfl_count: 0 });
    const totPerf = t.objectif ? (t.realise / t.objectif * 100).toFixed(1).replace(".", ",") : "";
    const totN1 = t.n_1 ? ((t.realise - t.n_1) / t.n_1 * 100).toFixed(1).replace(".", ",") : "";
    const totLflPct = t.lfl_n1 ? ((t.lfl_realise - t.lfl_n1) / t.lfl_n1 * 100).toFixed(1).replace(".", ",") : "";
    const totLflEur = t.lfl_count > 0 ? Math.round(t.lfl_realise - t.lfl_n1) : "";
    const totalRow = isGroup
      ? ["TOTAL", rows.length, Math.round(t.realise), Math.round(t.objectif), Math.round(t.realise - t.objectif), totPerf, ...jCols(t), totN1, totLflPct, totLflEur]
      : ["TOTAL", "", "", "", "", Math.round(t.realise), Math.round(t.objectif),
         Math.round(t.realise - t.objectif), totPerf, ...jCols(t), totN1, totLflPct, totLflEur];
    lines.push(totalRow.map(esc).join(sep));
    const csv = "\ufeff" + lines.join("\r\n"); // BOM for Excel FR
    const dateStr = selectedDate || new Date().toISOString().slice(0, 10);
    const fname = `orbit_ca_journalier_${viewMode}_${tableStatus}_${dateStr}.csv`;
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a"); a.href = url; a.download = fname;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Export réussi — ${rows.length} ligne${rows.length > 1 ? "s" : ""}`);
  }, [rowsForView, tableStatus, viewMode, selectedDate]);

  const onImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/kpi/ca-daily/import", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      let result = data;
      if (data?.status === "running" && data?.id?.startsWith("job_")) {
        toast.info("Import lancé… traitement en cours", { id: "ca-import-job" });
        for (;;) {
          await new Promise(r => setTimeout(r, 2000));
          const { data: j } = await api.get(`/import-jobs/${data.id}`);
          if (j.status === "done") { result = j.result || {}; break; }
          if (j.status === "error") throw new Error(j.error || "Échec de l'import");
        }
      }
      invalidate("/kpi/ca-daily");
      toast.dismiss("ca-import-job");
      toast.success(result?.message || "Import réussi", { duration: 12000 });
      if (result?.last_date_with_ca) toast.message(`Dernier jour avec CA réalisé dans le fichier : ${new Date(result.last_date_with_ca).toLocaleDateString("fr-FR")}`, { duration: 12000 });
      if (result?.name_warnings?.length) { setNameWarnings({ items: result.name_warnings, previous_month: result.previous_month }); toast.warning(`${result.name_warnings.length} nom(s) de corner diffèrent du mois précédent — vérifiez les fautes de frappe`, { duration: 12000 }); }
      api.post("/admin/import-logs", { kind: "ca_daily", status: "success", rows: result?.total_rows ?? null, filename: file.name, message: result?.message }).catch(() => {});
      setF({ ...f }); // trigger reload
    } catch (err) {
      toast.dismiss("ca-import-job");
      toast.error("Import échoué : " + (err.response?.data?.detail || err.message));
      api.post("/admin/import-logs", { kind: "ca_daily", status: "error", filename: file.name, message: err.response?.data?.detail || err.message }).catch(() => {});
    } finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="space-y-4" style={{ background: CLR.bg }} data-testid="kpi-ca-daily-v2-page">
      {nameWarnings && <NameWarningsDialog data={nameWarnings} onClose={() => setNameWarnings(null)} />}

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-[26px] font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-blue-600"/> Suivi CA Journalier
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">
            Suivi quotidien du chiffre d'affaires • Objectif • Réalisé • LFL • Analyse par corner
          </p>
          <LastImportInfo kinds={["ca_daily"]} className="mt-2"/><CreateReportButton module="ca_daily" className="mt-2 ml-2"/>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <>
              <button onClick={() => setSaisieOpen(true)} data-testid="cav2-saisie-btn"
                      className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold">
                <Edit className="w-4 h-4"/> Saisie CA
              </button>
              <Link to="/admin/import/ca_daily" data-testid="cav2-import-link" className="text-[11px] font-semibold text-blue-700 hover:underline">Imports → Administration</Link>
            </>
          )}
        </div>
      </div>

      {/* Onglets Vue synthétique / Vue détaillée */}
      <div className="flex items-center gap-1 border-b border-slate-200 flex-wrap" data-testid="cav2-tabs">
        {[["synthese", "Vue synthétique", LayoutGrid], ["detail", "Vue détaillée", Table2], ["analysis", "Analyse", BarChart3], ["forecast", "Projection & Simulation", Target]].map(([k, l, I]) => (
          <button key={k} onClick={() => setPageTab(k)} data-testid={`cav2-tab-${k}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${pageTab === k ? (k === "forecast" ? "border-violet-600 text-violet-700" : "border-blue-600 text-blue-700") : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <I className="w-4 h-4"/> {l}
          </button>
        ))}
        <PageActions title="Suivi CA Journalier" filters={() => document.querySelector("[data-testid=cav2-filters]")?.innerText?.replace(/\s+/g, " ").slice(0, 160) || ""} onAi={generateAi} aiLoading={aiLoading} aiLabel="Analyse IA du jour" onExcel={() => onExport()} testid="cav2-actions" />
      </div>

      {pageTab === "analysis" ? <><FilterBar meta={meta} f={f} setF={setF}/><CaAnalysis filters={f}/></> : pageTab === "forecast" ? <><FilterBar meta={meta} f={f} setF={setF}/><CaForecast filters={f}/></> : pageTab === "detail" ? <CaDetailView meta={meta} FilterBar={FilterBar}/> : <>

      {/* Filter bar */}
      <FilterBar meta={meta} f={f} setF={setF}/>

      {/* 6 KPI cards — même taille, une seule ligne desktop, sommes entières */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <KpiCard testid="kpi-realise" icon={Euro} label="CA Réalisé"
                 value={fmtEur(s.realise)}
                 sub={s.objectif ? `vs obj. ${fmtPct(perf - 1)}` : "—"}
                 subTone={perf >= 1 ? "up" : "down"}/>
        <KpiCard testid="kpi-objectif" icon={Target} iconColor="text-emerald-600" iconBg="bg-emerald-50"
                 label="Objectif" value={fmtEur(s.objectif)}
                 progress={perf} sub={s.objectif ? `${Math.round(perf * 100)}%` : "—"}/>
        <KpiCard testid="kpi-ecart" icon={TrendingUp} iconColor={ecart >= 0 ? "text-emerald-600" : "text-rose-600"} iconBg={ecart >= 0 ? "bg-emerald-50" : "bg-rose-50"}
                 label="Écart Objectif" value={(ecart >= 0 ? "+" : "") + fmtEur(ecart)}
                 sub={ecart >= 0 ? "au-dessus objectif" : "sous l'objectif"}
                 subTone={ecart >= 0 ? "up" : "down"}/>
        <KpiCard testid="kpi-lfl" icon={Percent} iconColor="text-blue-600" iconBg="bg-blue-50"
                 label="LFL" value={lflGlobal.pct == null ? "—" : fmtPct(lflGlobal.pct)}
                 sub={lflGlobal.eur == null
                   ? `vs N-1 à isopérimètre`
                   : `${lflGlobal.eur >= 0 ? "+" : ""}${fmtEur(lflGlobal.eur)} · ${lflGlobal.count} corner${lflGlobal.count > 1 ? "s" : ""}`}
                 subTone={(lflGlobal.pct || 0) >= 0 ? "up" : "down"}/>
        <KpiCard testid="kpi-moyjour" icon={Calendar} iconColor="text-indigo-600" iconBg="bg-indigo-50"
                 label="CA Moyen" value={fmtEur(moyJour)}
                 sub={`Mois: ${fmtEur(moyMois)}`}/>
        <KpiCard testid="kpi-projection" icon={TrendingUp} iconColor="text-emerald-600" iconBg="bg-emerald-50"
                 label="Projection fin de mois" value={fmtEur(projection)}
                 sub={s.objectif ? `vs obj. ${fmtPct((projection - s.objectif) / s.objectif)}` : "—"}
                 subTone={projection >= s.objectif ? "up" : "down"}/>
      </div>

      {/* Row 1 : Évolution CA + Heatmap sur la même ligne */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-[0_1px_3px_rgba(15,23,42,.04)]" data-testid="cav2-chart">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="font-display text-base font-bold text-slate-900">Évolution du CA quotidien</h2>
              <p className="text-[11px] text-slate-500">Réalisé, objectif et comparaison N-1</p>
            </div>
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
              {["jours","semaines","mois"].map(v => (
                <button key={v} onClick={() => setChartView(v)}
                        data-testid={`cav2-chart-${v}`}
                        className={`text-[11px] font-semibold px-3 py-1 rounded-md capitalize ${chartView === v ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>
                  {v}
                </button>
              ))}
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer>
              <ComposedChart data={chartData} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: CLR.muted }} axisLine={false} tickLine={false}/>
                <YAxis tickFormatter={fmtEurShort} tick={{ fontSize: 10, fill: CLR.muted }} axisLine={false} tickLine={false} width={55}/>
                <Tooltip
                  contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 6, fontSize: 12 }}
                  formatter={(v) => fmtEurShort(v)}
                  labelStyle={{ fontWeight: 600, color: CLR.text }}/>
                <Legend wrapperStyle={{ fontSize: 11 }} iconType="line"/>
                <Bar dataKey="N-1" fill="#E2E8F0" radius={[3,3,0,0]}/>
                <Line type="monotone" dataKey="Objectif" stroke={CLR.orange} strokeDasharray="5 3" strokeWidth={2} dot={false}/>
                <Line type="monotone" dataKey="Réalisé" stroke={CLR.blue} strokeWidth={2.5} dot={{ r: 3, fill: CLR.blue }} activeDot={{ r: 5 }}/>
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
        <HeatmapCard byCorner={byCorner} rows7d={data.rows || []} view={heatView} setView={setHeatView}/>
      </div>

      {/* Calendar strip */}
      <CalendarStrip byDay={byDay} selectedDate={selectedDate} setSelectedDate={setSelectedDate}/>

      {/* Table full-width with viewMode toggle */}
      <CornersTable
        corners={rowsForView}
        selectedDate={selectedDate}
        onRowClick={c => {
          if (viewMode === "corner") setDrilldown({ type: "corner", value: c.corner_key, label: c.corner });
          else if (viewMode === "global") setDrilldown({ type: "global", value: "all", label: c.corner });
          else setDrilldown({ type: viewMode, value: c._groupValue, label: c.corner });
        }}
        viewMode={viewMode}
        setViewMode={setViewMode}
        status={tableStatus}
        setStatus={setTableStatus}
        onExportView={(rows) => onExport(rows)}
      />

      {/* Row 2 : Top Performers + Sites à surveiller sur la même ligne */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TopPerformersCard topPerformers={topPerformers}/>
        <ASurveillerCard aSurveiller={aSurveiller}/>
      </div>

      {/* Row 3 : Répartition par enseigne + Analyse IA sur la même ligne */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DonutCard byCorner={byCorner}/>
        <InsightsIaCard aiInsights={aiInsights} aiLoading={aiLoading} onGenerateAi={generateAi}/>
      </div>
      </>}

      {/* Modal Saisie CA */}
      {saisieOpen && <SaisieCaModal onClose={() => setSaisieOpen(false)}/>}

      {/* Drilldown side panel */}
      {drilldown && (
        <DrilldownPanel
          target={drilldown}
          allRows={data.rows || []}
          weatherMap={weatherMap}
          onClose={() => setDrilldown(null)}
        />
      )}

      {loading && <div className="fixed bottom-4 right-4 bg-slate-900 text-white text-xs px-3 py-1.5 rounded-lg shadow-lg animate-pulse">Chargement…</div>}
    </div>
  );
}
